export type RdpClipboardMode = "none" | "local-to-remote" | "remote-to-local" | "bidirectional";
export type RdpCmdKeyMode = "local" | "windows";
export type RdpQualityProfile = "eco" | "standard" | "high" | "native";
export type RdpToolbarMode = "expanded" | "mini";

export interface RdpToolbarPosition {
  x: number;
  y: number;
}

export interface RdpBoxSize {
  width: number;
  height: number;
}

export function clipboardAllowsLocalToRemote(mode: RdpClipboardMode): boolean {
  return mode === "local-to-remote" || mode === "bidirectional";
}

export function clipboardAllowsRemoteToLocal(mode: RdpClipboardMode): boolean {
  return mode === "remote-to-local" || mode === "bidirectional";
}

export function isLocalPasteShortcut(event: Pick<KeyboardEvent, "code" | "ctrlKey" | "metaKey">): boolean {
  return event.code === "KeyV" && (event.ctrlKey || event.metaKey);
}

/**
 * Mode clipboard par défaut à la connexion RDP.
 *
 * `bidirectional` plutôt que `none` : la plupart des opérateurs attendent un
 * presse-papier qui marche sans configuration préalable. Le mode reste
 * modifiable en pleine session (les callbacks IronRDP interrogent le signal
 * live), donc un opérateur qui ne veut aucun transfert peut basculer sur `none`.
 */
export function defaultRdpClipboardMode(): RdpClipboardMode {
  return "bidirectional";
}

// ─────────────────────────────────────────────────────────────────────────────
// Synchronisation du Ctrl+V synthétisé avec la fin du handshake CLIPRDR.
//
// Le protocole RDP clipboard (MS-RDPECLIP §3.1.1 "Copy Sequence") impose :
//   1. Client  → CLIPRDR_FORMAT_LIST   (annonce des formats)
//   2. Serveur → CLIPRDR_FORMAT_DATA_REQUEST (demande du contenu)
//   3. Client  → CLIPRDR_FORMAT_DATA_RESPONSE (livraison du contenu)
//   4. Seulement ALORS le Ctrl+V peut coller le bon contenu côté Windows.
//
// Or `session.onClipboardPaste(data)` côté WASM ne fait qu'enfiler un message
// dans un channel unbounded puis résout sa Promise — bien avant que le FormatList
// PDU n'ait quitté le client. Si on envoie le Ctrl+V juste après, il arrive sur
// le serveur Windows avant que les données ne soient dans son presse-papier :
// collage vide ou stale. Race condition documentée (cf. GNOME Remote Desktop #44).
//
// On utilise donc `forceClipboardUpdateCallback` (invoqué par le serveur à
// l'étape 2, FormatDataRequest) comme **signal** fiable que le handshake a
// progressé, puis on attend un court garde-fou pour laisser le
// FormatDataResponse (étape 3) arriver. En dernier recours, un timeout global
// évite tout blocage infini si le serveur ne répond jamais.
// ─────────────────────────────────────────────────────────────────────────────

/** Garde-fou en ms après réception du signal serveur (FormatDataRequest → Response). */
export const RDP_CLIPBOARD_PASTE_GUARDRAIL_MS = 150;
/** Timeout global en ms : si le serveur n'a jamais demandé le contenu, on abandonne. */
export const RDP_CLIPBOARD_PASTE_TIMEOUT_MS = 4000;
/** Pas de polling en ms dans `waitForClipboardHandshake` (évite de surcharger le main thread). */
export const RDP_CLIPBOARD_PASTE_POLL_MS = 30;

export interface RemotePasteTimingInput {
  /** ms écoulées depuis l'appel à `session.onClipboardPaste()` (départ du handshake). */
  elapsedSincePasteMs: number;
  /** true si `forceClipboardUpdateCallback` a été invoqué depuis le début du paste. */
  serverRequestedContent: boolean;
  /** ms écoulées depuis le signal serveur (undefined si aucun signal). */
  elapsedSinceServerSignalMs?: number;
  /** Garde-fou en ms après le signal serveur. Défaut : `RDP_CLIPBOARD_PASTE_GUARDRAIL_MS`. */
  guardrailMs?: number;
  /** Timeout global en ms. Défaut : `RDP_CLIPBOARD_PASTE_TIMEOUT_MS`. */
  timeoutMs?: number;
}

export type RemotePasteTimingResult =
  | { kind: "ready" }
  | { kind: "wait"; waitMs: number }
  | { kind: "abort"; reason: "timeout" };

/**
 * Décide, à un instant T, si on peut envoyer le Ctrl+V synthétisé, s'il faut
 * encore attendre, ou si le handshake est mort et on abandonne.
 *
 * Fonction **pure** (pas d'IO, pas de timer) — entièrement testable sans WASM.
 * L'appelant (`waitForClipboardHandshake` dans `rdp.tsx`) l'interroge en boucle
 * avec un `setTimeout` de `RDP_CLIPBOARD_PASTE_POLL_MS` ms.
 */
export function resolveRemotePasteDelay(input: RemotePasteTimingInput): RemotePasteTimingResult {
  const guardrailMs = input.guardrailMs ?? RDP_CLIPBOARD_PASTE_GUARDRAIL_MS;
  const timeoutMs = input.timeoutMs ?? RDP_CLIPBOARD_PASTE_TIMEOUT_MS;

  // Cas 1 : le serveur a demandé le contenu (signal reçu) ET le garde-fou est écoulé
  // → le FormatDataResponse a eu le temps de partir, on peut coller.
  if (input.serverRequestedContent && (input.elapsedSinceServerSignalMs ?? 0) >= guardrailMs) {
    return { kind: "ready" };
  }

  // Cas 2 : timeout global dépassé → le serveur n'a jamais répondu, on abandonne.
  if (input.elapsedSincePasteMs >= timeoutMs) {
    return { kind: "abort", reason: "timeout" };
  }

  // Cas 3 : il faut encore attendre. On propose un temps d'attente raisonnable :
  //  - si on a un signal, attendre le reste du garde-fou ;
  //  - sinon, attendre jusqu'au timeout (mais l'appelant pollera de toute façon
  //    toutes les `POLL_MS` ms, donc on retourne juste le palier de polling).
  if (input.serverRequestedContent && input.elapsedSinceServerSignalMs !== undefined) {
    const remaining = guardrailMs - input.elapsedSinceServerSignalMs;
    return { kind: "wait", waitMs: Math.max(RDP_CLIPBOARD_PASTE_POLL_MS, remaining) };
  }
  return { kind: "wait", waitMs: RDP_CLIPBOARD_PASTE_POLL_MS };
}

export function defaultCmdKeyModeForPlatform(platform: string, userAgent = ""): RdpCmdKeyMode {
  return /Mac|iPhone|iPad|iPod/i.test(`${platform} ${userAgent}`) ? "local" : "windows";
}

export function shouldSuppressMetaKey(
  event: Pick<KeyboardEvent, "code" | "ctrlKey" | "metaKey">,
  mode: RdpCmdKeyMode,
): boolean {
  if (mode === "windows") return false;
  if (isLocalPasteShortcut(event)) return false;
  return event.code === "MetaLeft" || event.code === "MetaRight" || event.metaKey;
}

export function qualityScaleForProfile(profile: RdpQualityProfile, devicePixelRatio = 1): number {
  if (profile === "eco") return 0.6;
  if (profile === "standard") return 0.8;
  if (profile === "native") return Math.max(1, Math.min(2, devicePixelRatio || 1));
  return 1;
}

/**
 * Bornes du protocole Display Control (MS-RDPEDISP §2.2.2.2.2) pour la taille
 * d'un monitor primaire. IronRDP rejette silencieusement toute demande hors de
 * ces bornes (`encode_resize` → `None`, log WASM "Resize event ignored").
 *
 * Critique : la largeur **doit être paire** ("MUST NOT be an odd value").
 */
export const RDP_DESKTOP_MIN_WIDTH = 200;
export const RDP_DESKTOP_MIN_HEIGHT = 200;
export const RDP_DESKTOP_MAX_WIDTH = 8192;
export const RDP_DESKTOP_MAX_HEIGHT = 8192;

/**
 * Clampe une taille aux bornes RDP Display Control et force la largeur paire.
 *
 * Rappel MS-RDPEDISP §2.2.2.2.2 :
 *  - width  ∈ [200, 8192] et PAIRE (sinon le serveur rejette la demande) ;
 *  - height ∈ [200, 8192].
 * Une largeur impaire est la cause la plus fréquente de "resize silently
 * ignored" : la PDU part, le serveur la jette, rien ne se passe.
 */
export function clampRdpDesktopSize(size: RdpBoxSize): RdpBoxSize {
  const width = Math.max(RDP_DESKTOP_MIN_WIDTH, Math.min(RDP_DESKTOP_MAX_WIDTH, size.width));
  const height = Math.max(RDP_DESKTOP_MIN_HEIGHT, Math.min(RDP_DESKTOP_MAX_HEIGHT, size.height));
  // Force paire : 1023px → 1024px. On arrondit au pair supérieur pour ne pas
  // réduire la résolution demandée sous l'effet du clamp.
  return { width: width + (width % 2), height };
}

/**
 * Calcule la résolution RDP à demander au serveur pour une surface donnée.
 *
 * ── Contrat ──────────────────────────────────────────────────────────────
 * `surface` DOIT être exprimée en **pixels CSS (logiques)** du viewport
 * opérateur — c'est-à-dire la valeur brute de `getBoundingClientRect()`, SANS
 * multiplication par `devicePixelRatio`. Raison : la résolution RDP correspond
 * à la taille logique du bureau distant ; la multiplier par le DPR demanderait
 * un bureau 5K sur écran Retina pour une fenêtre 2560×1440 → le serveur encode
 * 4× trop de bitmaps → lenteur (RDP transporte des bitmaps bruts, contrairement
 * à la vidéo H.264 qui compresse).
 *
 * Les profils qualité multiplient ensuite cette mesure :
 * `eco`/`standard` sous-échantillonnent (moins de bande passante, le bureau est
 * zoomé visuellement par `object-fit: contain`), `native` conserve la densité
 * réelle de l'écran (utile pour la netteté si la bande passante le permet),
 * `high` est 1:1. Le résultat est clampé aux bornes RDP Display Control et sa
 * largeur est forcée paire.
 */
export function rdpDesktopSizeForSurface(
  surface: RdpBoxSize,
  profile: RdpQualityProfile,
  devicePixelRatio: number,
  limits: { minWidth: number; minHeight: number; maxWidth: number; maxHeight: number },
): RdpBoxSize {
  const scale = qualityScaleForProfile(profile, devicePixelRatio);
  const scaled: RdpBoxSize = {
    width: Math.max(limits.minWidth, Math.min(limits.maxWidth, Math.round(surface.width * scale))),
    height: Math.max(limits.minHeight, Math.min(limits.maxHeight, Math.round(surface.height * scale))),
  };
  return clampRdpDesktopSize(scaled);
}

export function clampRdpToolbarPosition(
  position: RdpToolbarPosition,
  bounds: RdpBoxSize,
  toolbar: RdpBoxSize,
  margin = 8,
): RdpToolbarPosition {
  const maxX = Math.max(margin, bounds.width - toolbar.width - margin);
  const maxY = Math.max(margin, bounds.height - toolbar.height - margin);
  return {
    x: Math.max(margin, Math.min(maxX, Math.round(position.x))),
    y: Math.max(margin, Math.min(maxY, Math.round(position.y))),
  };
}
